import { apiClient, type ApiResponse } from "./apiClient";

/** Feature keys under each admin-flags entry. */
export type AdminFeatureKey =
  | "auto_tagging"
  | "conditional_tagging"
  | "auto_tagging_result_view"
  | "conditional_tagging_result_view"
  | "triage_result_view"
  | "o2o_assessment";

export type AutoTaggingFeatureConfig = {
  max_plans_per_concept?: number;
};

export type ConditionalTaggingFeatureConfig = {
  max_conditions_total?: number;
};

export type AutoTaggingResultViewFeatureConfig = {
  display_concepts?: string[];
};

export type TriageResultViewFeatureConfig = Record<string, never> | Record<string, unknown>;

/** Empty object `{}` means plugged in with no nested settings. */
export type O2oAssessmentFeatureConfig = Record<string, never> | Record<string, unknown>;

/**
 * Per-feature config. `null` = not plugged in (hide tab).
 * `{}` or populated object = plugged in (show tab).
 */
export type AdminFlagFeatures = {
  auto_tagging: AutoTaggingFeatureConfig | null;
  conditional_tagging: ConditionalTaggingFeatureConfig | null;
  auto_tagging_result_view: AutoTaggingResultViewFeatureConfig | null;
  conditional_tagging_result_view: AutoTaggingResultViewFeatureConfig | null;
  triage_result_view: TriageResultViewFeatureConfig | null;
  o2o_assessment: O2oAssessmentFeatureConfig | null;
};

export type AdminFlagSubType = {
  code: string;
  name: string;
};

export type AdminFlag = {
  code: string;
  name: string;
  sub_types: AdminFlagSubType[];
  features: AdminFlagFeatures;
};

export type ObjectToObjectLimits = {
  max_objects_per_side?: number;
  max_dimensions_per_plan?: number;
  max_plans?: number;
};

/**
 * Normalized GET /admin-flags payload for the UI.
 * Flat O2O limit fields kept for existing consumers.
 */
export interface TenantConfig {
  flags: AdminFlag[];
  object_to_object: ObjectToObjectLimits;
  /** From `object_to_object.max_dimensions_per_plan`. */
  max_dimensions_per_plan?: number;
  /** From `object_to_object.max_objects_per_side`. */
  max_objects_per_side?: number;
  /** From `object_to_object.max_plans`. */
  max_plans?: number;
}

/** @deprecated Prefer {@link TenantConfig}. */
export type GlobalTenantConfig = TenantConfig;

export type TenantConfigResponse = ApiResponse<TenantConfig>;

/** @deprecated Prefer {@link TenantConfigResponse}. */
export type GlobalTenantConfigResponse = TenantConfigResponse;

export type FetchAdminFlagsOptions = {
  /** Optional filter; omit to load the full projected registry. */
  target_type?: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readPositiveNumber(
  record: Record<string, unknown> | null,
  key: string,
): number | undefined {
  if (!record) return undefined;
  const value = record[key];
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return undefined;
}

function readString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === "string" ? value.trim() : "";
}

function readStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
  return items;
}

/**
 * `null` = not plugged in. Missing key treated as null.
 * Present object (including `{}`) = plugged in.
 */
function readFeatureConfig<T extends Record<string, unknown>>(
  features: Record<string, unknown> | null,
  key: AdminFeatureKey,
  map: (record: Record<string, unknown>) => T,
): T | null {
  if (!features || !(key in features)) return null;
  const value = features[key];
  if (value === null) return null;
  const record = asRecord(value);
  if (!record) return null;
  return map(record);
}

function normalizeSubTypes(raw: unknown): AdminFlagSubType[] {
  if (!Array.isArray(raw)) return [];
  const items: AdminFlagSubType[] = [];
  for (const entry of raw) {
    const record = asRecord(entry);
    if (!record) continue;
    const code = readString(record, "code");
    if (!code) continue;
    items.push({
      code,
      name: readString(record, "name") || code,
    });
  }
  return items;
}

function normalizeFlagFeatures(
  raw: unknown,
): AdminFlagFeatures {
  const features = asRecord(raw);
  return {
    auto_tagging: readFeatureConfig(features, "auto_tagging", (record) => ({
      max_plans_per_concept: readPositiveNumber(record, "max_plans_per_concept"),
    })),
    conditional_tagging: readFeatureConfig(
      features,
      "conditional_tagging",
      (record) => ({
        max_conditions_total: readPositiveNumber(record, "max_conditions_total"),
      }),
    ),
    auto_tagging_result_view: readFeatureConfig(
      features,
      "auto_tagging_result_view",
      (record) => ({
        display_concepts: readStringArray(record.display_concepts),
      }),
    ),
    conditional_tagging_result_view: readFeatureConfig(
      features,
      "conditional_tagging_result_view",
      (record) => ({
        display_concepts: readStringArray(record.display_concepts),
      }),
    ),
    triage_result_view: readFeatureConfig(
      features,
      "triage_result_view",
      (record) => ({
        ...record,
      }),
    ),
    o2o_assessment: readFeatureConfig(features, "o2o_assessment", (record) => ({
      ...record,
    })),
  };
}

function normalizeAdminFlag(raw: unknown): AdminFlag | null {
  const record = asRecord(raw);
  if (!record) return null;
  const code = readString(record, "code");
  if (!code) return null;
  return {
    code,
    name: readString(record, "name") || code,
    sub_types: normalizeSubTypes(record.sub_types),
    features: normalizeFlagFeatures(record.features),
  };
}

function normalizeObjectToObjectLimits(
  raw: unknown,
): ObjectToObjectLimits {
  const record = asRecord(raw);
  return {
    max_objects_per_side: readPositiveNumber(record, "max_objects_per_side"),
    max_dimensions_per_plan: readPositiveNumber(record, "max_dimensions_per_plan"),
    max_plans: readPositiveNumber(record, "max_plans"),
  };
}

/**
 * Flatten GET /admin-flags into UI-friendly config.
 * Never treat `{}` as "off" — only explicit `null` means unplugged.
 */
export function normalizeTenantConfig(raw: unknown): TenantConfig | null {
  const root = asRecord(raw);
  if (!root) return null;

  const flags = Array.isArray(root.flags)
    ? root.flags
        .map(normalizeAdminFlag)
        .filter((item): item is AdminFlag => item !== null)
    : [];

  const objectToObject = normalizeObjectToObjectLimits(root.object_to_object);

  return {
    flags,
    object_to_object: objectToObject,
    max_dimensions_per_plan: objectToObject.max_dimensions_per_plan,
    max_objects_per_side: objectToObject.max_objects_per_side,
    max_plans: objectToObject.max_plans,
  };
}

/**
 * True when at least one flag plugs the feature in (`!== null`).
 * Empty object `{}` counts as plugged in.
 * When config is missing (load failed / no admin access), returns null so
 * callers can fall back to privilege-only tab visibility.
 */
export function isAdminFeatureEnabled(
  config: TenantConfig | null | undefined,
  feature: AdminFeatureKey,
): boolean | null {
  if (!config) return null;
  if (config.flags.length === 0) return false;
  return config.flags.some((flag) => flag.features[feature] !== null);
}

/** GET /admin-flags — projected tenant feature registry (normalized). */
export async function fetchTenantConfig(
  options?: FetchAdminFlagsOptions,
): Promise<TenantConfigResponse> {
  const params =
    options?.target_type?.trim()
      ? { target_type: options.target_type.trim() }
      : undefined;
  const result = await apiClient.get<unknown>("/admin-flags", params);
  const normalized = normalizeTenantConfig(result.data);
  return {
    ...result,
    data: normalized,
  };
}

/** @deprecated Prefer {@link fetchTenantConfig}. */
export async function fetchGlobalTenantConfig(): Promise<TenantConfigResponse> {
  return fetchTenantConfig();
}
